import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogTitle, Input, RadioGroup, Select } from '@/components/ui';
import { KR_BANKS, accountDigits } from '@/lib/domain/krBanks';
import type {
  AdminPaymentDetail,
  RefundCreateRequest,
  RefundReason,
} from '@/lib/api-admin/types';
import {
  REFUND_REASON_OPTIONS,
  createRefundFormSchema,
  type RefundAccountInput,
  type RefundFormValues,
} from '../lib/paymentSchema';
import {
  formatPaymentMinorAmount,
  minorToMajorString,
} from '@/features/orders/lib/chargeMoney';
import {
  EMPTY_REFUND_SCOPE,
  productRefundDefaultAmount,
  refundScopeBody,
  refundScopeError,
  type RefundScopeItem,
  type RefundScopeState,
} from '../lib/refundScope';
import { RefundScopeFields } from './RefundScopeFields';

/** QA-625 — generated 타입 반영 전 요청 확장(claimId·refundScope·items, BE payment #30). */
export type RefundActionBody = RefundCreateRequest & {
  claimId?: string | null;
  refundScope?: 'PRODUCT' | 'NON_PRODUCT';
  items?: Array<{ orderItemId: string; quantity: number }>;
};

export interface RefundActionModalProps {
  payment: AdminPaymentDetail;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (body: RefundActionBody) => void;
  onCancel: () => void;
  /**
   * 미리 채울 환불 계좌 — 클레임에서 진입했을 때 고객이 클레임에 남긴 계좌(클레임 상세 refundAccount).
   * 운영자가 확인·수정한 뒤 보낸다. 가상계좌 결제에서만 쓰인다.
   */
  prefillRefundAccount?: RefundAccountInput | null;
  /** QA-558 클레임에서 열 때 — 예상 환불액(major)으로 채운다. 남은 환불 가능액보다 크면 남은 금액. */
  defaultAmount?: number | null;
  /** QA-625 클레임에서 열었으면 그 클레임 — claimId 를 보내 그 클레임만 환불 완료가 된다. 환불 구분은 보내지 않는다. */
  claimId?: string | null;
  /** QA-625 환불 구분용 주문 상품(환불 가능 수량 1개 이상). 넘기면(클레임이 아닐 때) 환불 구분을 고르게 한다. */
  scopeItems?: ReadonlyArray<RefundScopeItem>;
}

// [pay-currency] AdminPaymentDetail additive charge* — be-snapshot 계약(major decimal STRING,
// 미지원 통화는 키 생략). generated 타입 미반영이라 로컬 확장.
type AdminPaymentDetailExtended = AdminPaymentDetail & {
  chargeAmount?: string | null;
  chargeRefundedAmount?: string | null;
  chargeRemainingRefundableAmount?: string | null;
};

// 통화 소수 자릿수 — BE CurrencyMinorUnits 미러(환불 입력 step/정밀도 검증용).
const DECIMAL_PLACES: Record<string, number> = { KRW: 0, USD: 2, JPY: 0, CNY: 2 };

export const RefundActionModal = ({
  payment,
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
  prefillRefundAccount = null,
  defaultAmount = null,
  claimId = null,
  scopeItems,
}: RefundActionModalProps) => {
  const { t } = useTranslation('payments');
  const ext = payment as AdminPaymentDetailExtended;
  const remainingMinor = payment.remainingRefundableAmount ?? payment.amount;
  // ★단위 계약: BE RefundCreateRequest.amount = 청구통화 **major** 단위(RefundServiceV2 가
  // CurrencyMinorUnits.toMinor 로 변환). detail 의 remainingRefundableAmount 는 minor 정수라
  // 입력/검증/기본값을 major 로 통일한다 — KRW/JPY 는 dp=0 이라 기존 숫자와 동일(회귀 0).
  // charge* 미제공(미배포)이면 minor→major 정확 변환 폴백, 미지원 통화면 기존 숫자 유지
  // (BE 가 어차피 통화 검증으로 거부 — 화면만 깨지지 않게).
  const dp = DECIMAL_PLACES[payment.currencyCode] ?? 0;
  const remainingMajorStr =
    ext.chargeRemainingRefundableAmount ??
    minorToMajorString(remainingMinor, payment.currencyCode);
  const remaining = remainingMajorStr != null ? Number(remainingMajorStr) : remainingMinor;
  const initialAmount = defaultAmount != null && defaultAmount > 0 ? Math.min(defaultAmount, remaining) : remaining;
  const requireRefundAccount = payment.paymentMethod === 'VIRTUAL_ACCOUNT';
  const [includeAccount, setIncludeAccount] = useState(requireRefundAccount);
  const emptyAccount = useMemo(
    () => prefillRefundAccount ?? { bankCode: '', accountNumber: '', holderName: '' },
    [prefillRefundAccount],
  );
  // 목록에 없는 코드(구버전 데이터 등)로 미리 채워져도 값이 사라지지 않게 선택지에 한 줄 붙인다.
  const bankOptions = useMemo(() => {
    const code = prefillRefundAccount?.bankCode;
    return code && !KR_BANKS.some((b) => b.code === code)
      ? [...KR_BANKS, { code, name: t('payments.refundModal.bankUnknown', { code, defaultValue: '코드 {{code}}' }) }]
      : KR_BANKS;
  }, [prefillRefundAccount, t]);

  const schema = useMemo(
    () =>
      createRefundFormSchema({
        remainingRefundableAmount: remaining,
        requireRefundAccount: includeAccount,
        maxDecimalPlaces: dp,
      }),
    [remaining, includeAccount, dp],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<RefundFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: initialAmount,
      reason: 'CHANGE_OF_MIND',
      detail: '',
      refundAccount: includeAccount ? { ...emptyAccount } : null,
    },
  });

  const showScope = scopeItems !== undefined && !claimId;
  const [scope, setScope] = useState<RefundScopeState>(EMPTY_REFUND_SCOPE);
  const [scopeTouched, setScopeTouched] = useState(false);
  const scopeErr = showScope && scopeTouched ? refundScopeError(scope) : null;
  // 상품을 고르면 청구통화 단가 × 수량을 기본 금액으로(남은 환불 가능액까지). 운영자가 고칠 수 있다.
  const changeScope = (next: RefundScopeState) => {
    setScope(next);
    if (next.scope !== 'PRODUCT') return;
    const amount = productRefundDefaultAmount(scopeItems ?? [], next, payment.currencyCode, dp);
    if (amount != null) setValue('amount', Math.min(amount, remaining));
  };

  // 열 때마다(그리고 닫을 때) 초기값으로 맞춘다 — 클레임 계좌는 비동기로 오므로 열리는 시점의 값으로 채운다.
  useEffect(() => {
    reset({
      amount: initialAmount,
      reason: 'CHANGE_OF_MIND',
      detail: '',
      refundAccount: requireRefundAccount ? { ...emptyAccount } : null,
    });
    setIncludeAccount(requireRefundAccount);
    setScope(EMPTY_REFUND_SCOPE);
    setScopeTouched(false);
  }, [isOpen, initialAmount, requireRefundAccount, emptyAccount, reset]);

  const submit = handleSubmit((values) => {
    if (showScope && refundScopeError(scope)) {
      setScopeTouched(true);
      return;
    }
    const body: RefundActionBody = {
      amount: values.amount,
      currencyCode: payment.currencyCode,
      reason: values.reason as RefundReason,
      detail:
        values.detail && values.detail.length > 0
          ? values.detail.trim()
          : null,
      refundAccount:
        includeAccount && values.refundAccount
          ? {
              bankCode: values.refundAccount.bankCode.trim(),
              // 하이픈·공백은 빼고 숫자만 보낸다(BE 도 숫자만 남긴다).
              accountNumber: accountDigits(values.refundAccount.accountNumber),
              holderName: values.refundAccount.holderName.trim(),
            }
          : null,
      ...(claimId ? { claimId } : showScope ? refundScopeBody(scope) : {}),
    };
    onSubmit(body);
  });

  const amountErrorKey =
    errors.amount?.message === 'amount.exceeds_refundable'
      ? 'payments.refundModal.amountExceedsRefundable'
      : errors.amount?.message === 'amount.positive'
        ? 'payments.refundModal.amountPositive'
        : errors.amount?.message === 'amount.precision'
          ? 'payments.refundModal.amountPrecision'
          : errors.amount
            ? 'payments.refundModal.amountInvalid'
            : null;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogTitle>{t('payments.refundModal.title')}</DialogTitle>
        <dl className="mt-3 grid grid-cols-2 gap-1 text-sm">
          <dt className="text-[color:var(--content-muted)]">
            {t('payments.refundModal.totalAmount')}
          </dt>
          <dd className="tabular-nums text-[color:var(--content-strong)]">
            {formatPaymentMinorAmount(payment.amount, payment.currencyCode, ext.chargeAmount)}
          </dd>
          <dt className="text-[color:var(--content-muted)]">
            {t('payments.refundModal.refundedAmount')}
          </dt>
          <dd className="tabular-nums text-[color:var(--content-strong)]">
            {formatPaymentMinorAmount(
              payment.refundedAmount ?? 0,
              payment.currencyCode,
              ext.chargeRefundedAmount,
            )}
          </dd>
          <dt className="text-[color:var(--content-muted)]">
            {t('payments.refundModal.refundable')}
          </dt>
          <dd className="tabular-nums text-[color:var(--content-strong)]">
            {formatPaymentMinorAmount(
              remainingMinor,
              payment.currencyCode,
              ext.chargeRemainingRefundableAmount,
            )}
          </dd>
        </dl>

        {/* noValidate — 검증은 zod(RHF resolver)가 전담. 네이티브 constraint validation 은
            소수 step(0.01)의 float stepMismatch 로 유효한 외화 금액 제출까지 막는다. */}
        <form onSubmit={submit} className="mt-4 space-y-4" noValidate>
          {showScope ? (
            <RefundScopeFields
              idPrefix="refund"
              items={scopeItems ?? []}
              state={scope}
              onChange={changeScope}
              error={scopeErr}
            />
          ) : null}

          <div className="flex flex-col">
            <label
              htmlFor="refund-amount"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('payments.refundModal.amount')}
            </label>
            <Input
              id="refund-amount"
              type="number"
              // [pay-currency] major 단위 입력 — USD/CNY 는 센트 자리(0.01), KRW/JPY 는 정수.
              step={dp === 2 ? '0.01' : '1'}
              min={dp === 2 ? 0.01 : 1}
              max={remaining}
              {...register('amount', { valueAsNumber: true })}
            />
            {amountErrorKey ? (
              <p
                className="mt-1 text-xs"
                role="alert"
                data-testid="refund-amount-error"
                style={{ color: 'var(--status-danger-fg)' }}
              >
                {t(amountErrorKey, {
                  remaining: formatPaymentMinorAmount(
                    remainingMinor,
                    payment.currencyCode,
                    ext.chargeRemainingRefundableAmount,
                  ),
                  decimals: dp,
                })}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <span
              id="refund-reason-label"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('payments.refundModal.reason')}
            </span>
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  options={REFUND_REASON_OPTIONS.map((r) => ({
                    value: r,
                    label: t(`payments.refundReason.${r}`),
                  }))}
                  orientation="horizontal"
                  aria-labelledby="refund-reason-label"
                  data-testid="refund-reason"
                />
              )}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="refund-detail"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('payments.refundModal.detail')}
            </label>
            <textarea
              id="refund-detail"
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: 'var(--surface-1)',
                background: 'var(--surface-0)',
                color: 'var(--content-strong)',
              }}
              rows={3}
              maxLength={500}
              {...register('detail')}
            />
          </div>

          {requireRefundAccount ? (
            <fieldset
              className="rounded-md border p-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <legend className="px-1 text-sm font-medium text-[color:var(--content-strong)]">
                {t('payments.refundModal.refundAccount')}
              </legend>
              <p className="mt-1 text-xs text-[color:var(--content-muted)]" data-testid="refund-toss-transfer-hint">
                {t('payments.refundModal.tossTransferHint', '입력한 계좌로 Toss 가 환불금을 송금합니다.')}
              </p>
              {prefillRefundAccount ? (
                <p className="mt-1 text-xs text-[color:var(--content-muted)]" data-testid="refund-account-prefilled">
                  {t(
                    'payments.refundModal.prefilledFromClaim',
                    '고객이 클레임에 남긴 환불 계좌를 채웠습니다. 확인 후 환불하세요.',
                  )}
                </p>
              ) : null}
              <div className="mt-2 grid grid-cols-3 gap-2">
                {/* 은행은 금융결제원 3자리 코드로 보낸다 — 쇼핑몰 은행 목록과 같은 선택지. */}
                <Select
                  aria-label={t('payments.refundModal.bankCode')}
                  data-testid="refund-bank"
                  {...register('refundAccount.bankCode')}
                >
                  <option value="">{t('payments.refundModal.bankSelect', '은행 선택')}</option>
                  {bankOptions.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </Select>
                <Input
                  inputMode="numeric"
                  placeholder={t('payments.refundModal.accountNumber')}
                  aria-label={t('payments.refundModal.accountNumber')}
                  {...register('refundAccount.accountNumber')}
                />
                <Input
                  placeholder={t('payments.refundModal.holderName')}
                  aria-label={t('payments.refundModal.holderName')}
                  {...register('refundAccount.holderName')}
                />
              </div>
              {errors.refundAccount ? (
                <p
                  className="mt-1 text-xs"
                  role="alert"
                  style={{ color: 'var(--status-danger-fg)' }}
                >
                  {t('payments.refundModal.refundAccountRequired')}
                </p>
              ) : null}
            </fieldset>
          ) : null}

          {errorMessage ? (
            <p className="text-sm" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('payments.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="md"
              disabled={isSubmitting || remaining <= 0}
              data-action="refund-confirm"
            >
              {isSubmitting
                ? t('payments.refundModal.submitting')
                : t('payments.refundModal.confirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
